/**
 * Image cropping utilities
 * Handles canvas-based image cropping with quality control
 */

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Create an HTMLImageElement from a URL
 */
export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Handle CORS for external images
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

/**
 * Extract a cropped region from an image
 * @param image - HTMLImageElement to crop
 * @param pixelCrop - Crop area in pixels (based on displayed image size)
 * @param quality - JPEG quality (0-1), default 0.95
 * @returns Cropped image as Blob
 */
export async function getCroppedImg(
  image: HTMLImageElement,
  pixelCrop: CropArea,
  quality: number = 0.95
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // CRITICAL: Scale crop coordinates from displayed size to natural size
  // react-image-crop gives coordinates based on displayed (CSS-scaled) image,
  // but we need to crop the natural (full-resolution) image.
  // 
  // Example:
  // - Natural: 3000×2000px, Displayed: 300×200px → scaleX = 10, scaleY = 10
  // - Crop box: {x: 50, y: 50, w: 100, h: 100} in display coords
  // - Natural coords: {x: 500, y: 500, w: 1000, h: 1000}
  
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  
  // Scale crop coordinates to natural image dimensions
  const scaledCrop = {
    x: pixelCrop.x * scaleX,
    y: pixelCrop.y * scaleY,
    width: pixelCrop.width * scaleX,
    height: pixelCrop.height * scaleY,
  };

  // Set canvas size to scaled crop dimensions
  canvas.width = scaledCrop.width;
  canvas.height = scaledCrop.height;

  // Draw the cropped portion using natural coordinates
  ctx.drawImage(
    image,
    scaledCrop.x,
    scaledCrop.y,
    scaledCrop.width,
    scaledCrop.height,
    0,
    0,
    scaledCrop.width,
    scaledCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Get rotation in degrees for canvas transformation
 */
export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Rotate and crop image (for future enhancement)
 */
export async function getRotatedImage(
  imageSrc: string,
  rotation: number = 0
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  const orientationChanged =
    rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270;
  
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  return canvas.toDataURL('image/jpeg', 0.95);
}

